import type { ProductLabel } from '../../../../product/index';
import { getCardDiscountPercentage } from '@vs-shop/domain/price/getDiscountPercentageFromPrices';
import type { Option } from 'effect';

import {
  ProductCardAgeBadge,
  ProductCardDiscountBadge,
  ProductCardLabels,
} from '@vs-shop/commerce-ui/productCard';
import { ProductLabelVariant } from '@vs-shop/commerce-ui/productLabel';

import { age18 } from '../../../../config/checkout';

import { IconAgeRestriction } from '@vs-shop/ui/icons';
import { IconDiscountBadge } from '../../icon/IconDiscountBadge';
import { IntlPercentage } from '../../intl/Intl';
import { ProductLabels } from '../../page/product/gallery/ProductLabels';
import { cardLabels } from '../../page/product/gallery/productLabelPlacement';

type Props = {
  labels: readonly ProductLabel[];
  ageRestriction: 'none' | '18+';
  /** `None` when the variant carries no channel price — never fabricated as `0`. */
  price: Option.Option<number>;
  /** Raw `number | null` until `ProductCardData` ships branded money (ledger debt). */
  originalPrice: number | null;
};

/**
 * What rcprofi hangs in the card's media box.
 *
 * The three positions are the section's — leading top, trailing top, trailing
 * bottom — and what fills each is this tenant's: its label set, its discount
 * medallion, its age mark. A brand with a season sticker and no discount badge
 * composes the same parts with different contents.
 */
export default function ProductCardStickers({
  labels,
  ageRestriction,
  price,
  originalPrice,
}: Props) {
  const discountPercentage = getCardDiscountPercentage(price, originalPrice);

  const hasAgeRestriction = ageRestriction === age18;

  return (
    <>
      <ProductCardLabels>
        <ProductLabels labels={cardLabels(labels)} variant={ProductLabelVariant.COMPACT} />
      </ProductCardLabels>

      {discountPercentage !== null && (
        <ProductCardDiscountBadge>
          <IconDiscountBadge>
            &#8208;
            <IntlPercentage value={String(discountPercentage)} />
          </IconDiscountBadge>
        </ProductCardDiscountBadge>
      )}

      {hasAgeRestriction && (
        <ProductCardAgeBadge>
          <IconAgeRestriction className="size-[50px]" />
        </ProductCardAgeBadge>
      )}
    </>
  );
}
